import type {
  CertificationReleaseId,
  CertificationScheme,
  CertificationViewport,
} from "./required-ui-block-family-certification"
import type { DesignOntologyRelease } from "./design-ontology-live-swap"
import type { RequiredUiBlockFamilyEvidenceSource } from "./required-ui-block-family-live-swap"
import type { RequiredUiBlockQualityObservation } from "./required-ui-block-family-certification-quality"

export const REQUIRED_UI_BLOCK_CERTIFICATION_PROTOCOL_VERSION =
  "cogo.required-ui-block-family-certification-protocol.v1" as const

export interface RequiredUiBlockCertificationArtifactInput {
  artifactRef: string
  artifactHash: `sha256:${string}`
  initialState?: Partial<Record<"local" | "props" | "appData" | "computed", Record<string, unknown>>>
  documents: {
    block: Record<string, unknown>
    datasource?: Record<string, unknown>
    events: Record<string, unknown>
    flow: Record<string, unknown>
    ui: Record<string, unknown>
    variables: Record<string, unknown>
  }
}

export interface RequiredUiBlockCertificationSubjectInput {
  schemaVersion: "cogo.required-ui-block-family-certification-subject-input.v1"
  family: RequiredUiBlockFamilyEvidenceSource
  artifacts: RequiredUiBlockCertificationArtifactInput[]
  releases: DesignOntologyRelease[]
}

export interface RequiredUiBlockCertificationPresentation {
  releaseId: CertificationReleaseId
  scheme: CertificationScheme
  viewport: CertificationViewport
  width: number
  height: number
}

export type RequiredUiBlockCertificationCommand =
  | { type: "mount" }
  | { type: "seed" }
  | { type: "interact" }
  | ({ type: "set-presentation" } & RequiredUiBlockCertificationPresentation)
  | { type: "observe" }
  | { type: "dispose" }

export interface RequiredUiBlockCertificationControlSnapshot {
  elementId: string
  tagName: string
  type: string
  value: string | boolean
  selectionStart: number | null
  selectionEnd: number | null
}

export interface RequiredUiBlockCertificationArtifactSubjectObservation {
  artifactRef: string
  artifactHash: `sha256:${string}`
  mountId: string
  rendererNodeIdentity: string
  structuralFingerprint: string
  semanticElementIds: string[]
  eventTargets: string[]
  controls: RequiredUiBlockCertificationControlSnapshot[]
  focusedElementId: string | null
  scrollPositions: Record<string, number>
  actionFlowTrace: Array<Record<string, unknown>>
  visualSignature: string
  resolvedDesignRoles: Record<string, string>
  visualProbes: Record<string, string>
  quality: RequiredUiBlockQualityObservation
  unresolvedDesignAuthorityCount: number
  unknownElementCount: number
}

export interface RequiredUiBlockCertificationSubjectObservation {
  schemaVersion: "cogo.required-ui-block-family-certification-subject-observation.v1"
  protocolVersion: typeof REQUIRED_UI_BLOCK_CERTIFICATION_PROTOCOL_VERSION
  patternId: string
  presentation: RequiredUiBlockCertificationPresentation
  artifacts: RequiredUiBlockCertificationArtifactSubjectObservation[]
}

export interface RequiredUiBlockCertificationCommandResult {
  protocolVersion: typeof REQUIRED_UI_BLOCK_CERTIFICATION_PROTOCOL_VERSION
  command: RequiredUiBlockCertificationCommand["type"]
  ok: boolean
  blockers: string[]
  observation?: RequiredUiBlockCertificationSubjectObservation
}

export interface RequiredUiBlockCertificationBrowserBridge {
  protocolVersion: typeof REQUIRED_UI_BLOCK_CERTIFICATION_PROTOCOL_VERSION
  command(
    command: RequiredUiBlockCertificationCommand,
  ): Promise<RequiredUiBlockCertificationCommandResult>
}

declare global {
  interface Window {
    __cogoRequiredUiBlockCertification?: RequiredUiBlockCertificationBrowserBridge
  }
}
